import React, { ReactElement, useEffect, useRef, useState } from 'react'

import styled from 'styled-components'
import moment, { Moment } from 'moment'
import Datetime from 'react-datetime'
import reactDatetimeCss from '!!raw-loader!react-datetime/css/react-datetime.css'

import { useThemeContext } from '@intelie/ui'

export const formatDate = 'YYYY/MM/DD HH:mm'
interface CustomDateTimePickerProps {
    date: Record<number, Date | string | Moment>
    setDate: (rowId: number, date: Date | string | Moment) => void
    rowId: number
    style?: { borderColor: string }
    isDisabled?: boolean
}

export const CustomDateTimePicker = ({
    date,
    setDate,
    rowId,
    style,
    isDisabled
}: CustomDateTimePickerProps): ReactElement => {
    const [inputValue, setInputValue] = useState<string>(date ? moment(date, formatDate).format(formatDate) : '')
    const [initialViewDate, setInitialViewDate] = useState<Moment | null>(null)

    const calendarRef = useRef<Datetime>(null)
    const liveTheme = useThemeContext()?.theme?.themeName

    const titleTootip = `${inputValue ? moment(inputValue, formatDate).format(`${formatDate} Z`) : inputValue}`

    const dateRange = (current: Moment): boolean => {
        return current.isBetween(moment(13010321, formatDate), moment(), null, '[]')
    }

    const handleDateChange = (date: string | Moment): void => {
        const newDate = moment(date, formatDate).format(formatDate)
        setDate(rowId, date instanceof moment ? date.toDate() : newDate)
        setInputValue(newDate)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const newValue = e.target.value
        setInputValue(newValue)

        const newDate = moment(newValue, formatDate, true).format(formatDate)
        setDate(rowId, newDate)
    }

    const renderInput = (
        props: React.InputHTMLAttributes<HTMLInputElement>,
        openCalendar: () => void
    ): ReactElement => {
        return (
            <input
                {...props}
                onFocus={() => {
                    openCalendar()
                    if (!date && calendarRef.current) {
                        calendarRef.current.setState({ viewDate: initialViewDate })
                    }
                }}
                title={titleTootip}
                onChange={handleInputChange}
                value={inputValue}
                placeholder="Choose a date"
            />
        )
    }

    useEffect(() => {
        setInitialViewDate(moment())
    }, [])

    return (
        <>
            <CustomDateTimePickerContainer isDisabled={isDisabled} liveTheme={liveTheme}>
                <div style={style} className={`custom-input-wrapper ${isDisabled ? 'isDisabled' : ''}`}>
                    <Datetime
                        ref={calendarRef}
                        value={date as string}
                        onChange={handleDateChange}
                        dateFormat="YYYY/MM/DD"
                        timeFormat="HH:mm"
                        renderInput={renderInput}
                        isValidDate={dateRange}
                        closeOnSelect
                    />
                    <i className="fas fa-calendar-alt" />
                </div>
            </CustomDateTimePickerContainer>
        </>
    )
}

const CustomDateTimePickerContainer = styled.div<{ isDisabled?: boolean; liveTheme: string }>`
    ${reactDatetimeCss}

    display: flex;
    flex-direction: column;
    position: relative;

    .custom-input-wrapper {
        height: 32px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        padding: 16px 8px;
        border-radius: 3px;
        cursor: pointer;

        border: 1px solid var(--gray-400);
        color: var(--gray-800);
        background-color: var(--gray-0);

        &.isDisabled {
            color: var(--gray-700) !important;
            background-color: ${({ liveTheme }) =>
                liveTheme === 'dark' ? 'var(--gray-300)' : 'var(--gray-200)'} !important;
            box-sizing: border-box;
            border-radius: 3px;
            pointer-events: none;
        }
        i {
            position: absolute;
            right: 11px;
            color: var(--blue-500);
        }

        .rdt {
            .form-control {
                width: 105px;
                padding: 0;

                font-size: 12px;
                line-height: 18px;

                border: none;
                cursor: pointer;

                color: ${({ isDisabled }) => (isDisabled ? 'var(--gray-700)' : 'var(--gray-800)')};
                background-color: transparent;

                &:placeholder {
                    color: var(--gray-600);
                }
            }

            .rdtPicker {
                min-width: 198px;
                border-radius: 3px;
                border: 1px solid var(--gray-400);
                background-color: var(--gray-0);

                .rdtTime {
                    .rdtCounters {
                        .rdtBtn,
                        .rdtCount,
                        .rdtBtn {
                            &:hover {
                                border-radius: 3px;
                                background-color: var(--gray-200);
                            }
                        }
                    }
                }

                .rdtDays {
                    thead tr,
                    tbody tr {
                        height: 20px;
                    }
                    thead {
                        position: relative;
                        tr {
                            th {
                                border: none;
                                vertical-align: middle;
                                text-align: center;
                                padding: 4px;
                                background-color: var(--gray-0);
                            }
                        }
                        .dow {
                            font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
                            font-size: 12px;
                            font-weight: 500;
                            color: var(--gray-1000);
                        }
                    }
                    thead::after {
                        content: '';
                        position: absolute;
                        bottom: 0;
                        width: 100%;
                        height: 1px;
                        background: var(--gray-300);
                    }
                    tbody {
                        position: relative;
                        tr {
                            td {
                                border: none;
                                vertical-align: middle;
                                text-align: center;

                                &:not(.rdtDisabled):not(.rdtActive) {
                                    color: var(--gray-1000) !important;
                                }
                                &.rdtDisabled {
                                    color: var(--gray-400);
                                }

                                &.rdtActive {
                                    background-color: #216ba5;
                                    color: var(--gray-0);
                                }
                                &.rdtActive:hover {
                                    background-color: #12456d;
                                    color: var(--gray-0);
                                }
                            }
                        }
                    }

                    tbody::after {
                        content: '';
                        position: absolute;
                        bottom: 0px;
                        width: 100%;
                        height: 1px;
                        background: var(--gray-300);
                    }

                    tfoot {
                        tr::before {
                            content: 'Hour: ';
                            vertical-align: middle;
                            display: flex;
                            align-items: center;
                            height: 40px;
                            font-size: 13px;
                            font-weight: 600;
                            color: var(--gray-800);
                        }
                        tr {
                            td.rdtTimeToggle {
                                position: relative;
                                border: none !important;
                                border-radius: 3px;
                                padding: 2px 2px 2px 16px !important;
                                text-align: left !important;
                                vertical-align: middle !important;
                                color: var(--gray-800) !important;
                            }
                            td.rdtTimeToggle::before {
                                content: '';
                                width: 50px;
                                height: 24px;
                                border-radius: 3px;
                                border: 1px solid var(--gray-400);
                                position: absolute;
                                top: 8px;
                                left: 6px;
                            }
                        }
                    }

                    .rdtSwitch {
                        font-size: 1.1em;
                        color: var(--gray-1000);
                    }

                    .rdtTimeToggle {
                        background-color: var(--gray-0);

                        &:hover {
                            background-color: var(--gray-200);
                        }
                    }

                    .rdtDay:hover {
                        border-radius: 3px;
                        background-color: var(--gray-200);
                    }

                    .rdtActive {
                        border-radius: 0.3rem;

                        color: white;
                        background-color: var(--blue-500);
                    }

                    .rdtToday::before {
                        border-bottom: none;
                    }
                }
            }
        }

        table {
            thead {
                tr {
                    th {
                        border: none;
                    }
                }
            }
        }
    }
`
